
import {
  defineComponent,
  PropType,
  ref,
  useAttrs,
  computed
} from '@vue/composition-api';
import { BaseEdgeProps } from '../../types';
import EdgeText from './EdgeText.vue';
import { CSSProperties } from '@/components/types';
import {
  Group,
  groupsProvider
} from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';

import {
  ExpressionPredicateNodeTree,
  IWorkflowState,
  IWorkflowTransition,
  IWorkflowTransitionNode,
  TransitionNodeBinaryOperators,
  TransitionNodeOperandSubtype
} from '@/models';

export default defineComponent({
  name: 'BaseEdge',

  components: {
    EdgeText
  },

  props: {
    id: String,
    labelX: Number,
    labelY: Number,
    path: String,
    label: String,
    markerStart: String,
    markerEnd: String,

    /** Label styles (CSSProperties) */
    labelStyle: Object as PropType<CSSProperties>,
    /** Show label bg */
    labelShowBg: Boolean,
    /** Label Bg styles (CSSProperties) */
    labelBgStyle: Object as PropType<CSSProperties>,
    /** Label Bg padding */
    labelBgPadding: Array as PropType<number[]>,
    /** Label Bg border radius */
    labelBgBorderRadius: Number,

    interactionWidth: {
      type: Number,
      default: 20
    },

    data: Object as PropType<IWorkflowTransition>
  },

  setup(props: BaseEdgeProps<IWorkflowTransition>) {
    const attrs = useAttrs();
    const labelEl = ref<SVGGElement | null>(null);
    const pathEl = ref<SVGPathElement | null>(null);
    const interactionEl = ref<SVGPathElement | null>(null);

    const labelText = computed(
      () => toTransitionStringRepresentation(props.data!) ?? props.label
    );

    const store = useStore<IRootState>();

    const groupsStoreProvider = computed<any>(() => groupsProvider(store));

    const groups = computed<Group[]>(
      () => groupsStoreProvider.value.getPermissionGroups
    );

    function toTransitionStringRepresentation(
      transition: IWorkflowTransition
    ): string {
      let str = transition.toString().toUpperCase();

      if (transition.nodes.length > 0) {
        str += `\n[${expressionTreeToString(
          transition.toExpressionTree(),
          transition.source_state as IWorkflowState
        )}]`;
      }

      if (transition.actions.length > 0) {
        str += `\n/${transition.actions.map(action => action.name).join('')}`;
      }

      return str;
    }

    function expressionTreeToString(
      tree: ExpressionPredicateNodeTree<IWorkflowTransitionNode>,
      state: IWorkflowState,
      depth = 0
    ): string {
      return tree
        .map(node => {
          if (Array.isArray(node)) {
            // Recursively process subgroups
            const expr = expressionTreeToString(node, state, depth + 1);
            return expr;
          } else {
            // Leaf node: use its toString method
            const expr = nodeStringRepresentation(node, state);
            return depth < 1 ? `${expr}\n` : expr;
          }
        })
        .join(' ');
    }

    function nodeStringRepresentation(
      node: IWorkflowTransitionNode,
      state: IWorkflowState
    ): string {
      if (node.isOperator()) {
        switch (node.subtype) {
          case TransitionNodeBinaryOperators.StartBracket:
            return '(';
          case TransitionNodeBinaryOperators.CloseBracket:
            return ')';
          case TransitionNodeBinaryOperators.Equals:
            return '==';
          case TransitionNodeBinaryOperators.NotEqual:
            return '!=';
          case TransitionNodeBinaryOperators.GreaterThan:
            return '>';
          case TransitionNodeBinaryOperators.GreaterThanEqual:
            return '>=';
          case TransitionNodeBinaryOperators.LessThan:
            return '<';
          case TransitionNodeBinaryOperators.LessThanEqual:
            return '<=';
          case TransitionNodeBinaryOperators.And:
            return '&&';
          case TransitionNodeBinaryOperators.Or:
            return '||';
          case TransitionNodeBinaryOperators.Not:
            return '!';
          default:
            return `Unknown(${node.subtype})`;
        }
      } else {
        const hashOperandValue: Record<TransitionNodeOperandSubtype, string> = {
          [TransitionNodeOperandSubtype.Constant]: `${node.constant}`,
          [TransitionNodeOperandSubtype.OwnerGroup]: `Owner Group == (${groups.value
            ?.filter(group => node.groups.includes(group.id!))
            .map(group => group.name)
            .join(', ')})`,
          [TransitionNodeOperandSubtype.AssigneeGroup]: `Assignee Group == (${groups.value
            ?.filter(group => node.groups.includes(group.id!))
            .map(group => group.name)
            .join(', ')})`,
          [TransitionNodeOperandSubtype.Threshold]: `Threshold == ${state?.threshold_groups
            .find(
              threshold_group => threshold_group.id === node.threshold_group
            )
            ?.toString()}`,
          [TransitionNodeOperandSubtype.Variable]: `${node.variable_name}`
        };

        return (
          hashOperandValue[node.subtype as TransitionNodeOperandSubtype] ||
          `Unknown(${node.subtype})`
        );
      }
    }

    return {
      labelText,
      attrs,
      labelEl,
      pathEl,
      interactionEl
    };
  }
});
